import { useRef, useState, type ReactNode } from 'react';

import {
  ChevronDown,
  ChevronRight,
  X,
  MessageCircleQuestion,
  Wallet,
  TrendingUp,
  PiggyBank,
  RefreshCw,
  Shield,
} from 'lucide-react';

import { Drawer } from '../../primitives/Drawer';
import { Mascot } from '../../primitives/Mascot';
import { cn } from '../../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HelpCta {
  label: string;
  path: string;
}

export interface HelpFaqItem {
  id: string;
  question: string;
  answer: ReactNode;
  cta?: HelpCta;
  /** ID del tour a lanzar cuando el usuario quiere ver el paso a paso */
  tourId?: string;
}

export interface HelpSection {
  id: string;
  icon: ReactNode;
  title: string;
  items: HelpFaqItem[];
  /** 'ghost' = no bg, default text (default) | 'primary' = solid primary button */
  iconVariant?: 'ghost' | 'primary';
}

export interface HelpChatProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (path: string) => void;
  onStartTour?: (tourId: string) => void;
  sections?: HelpSection[];
  /** Section id to auto-expand when the panel opens */
  initialSectionId?: string;
  greeting?: string;
  className?: string;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const ic = 'h-5 w-5';

// ─── Default FAQ content ──────────────────────────────────────────────────────

export const DEFAULT_HELP_SECTIONS: HelpSection[] = [
  {
    id: 'wallet',
    icon: <Wallet className={ic} />,
    title: 'Mi Wallet',
    items: [
      {
        id: 'wallet-deposit',
        tourId: 'deposit',
        question: '¿Cómo deposito dinero?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Wallet</strong> en el menú lateral
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca el botón{' '}
              <strong>Depositar</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Copia tu CLABE
              interbancaria de 18 dígitos
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Haz una transferencia desde
              tu banco a esa CLABE
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">5.</span> El saldo aparece en minutos
            </li>
          </ol>
        ),
        cta: { label: 'Ir a mi Wallet', path: '/wallet' },
      },
      {
        id: 'wallet-transfer',
        tourId: 'transfers',
        question: '¿Cómo transfiero dinero a alguien?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Transferir</strong> en el menú
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Busca al destinatario por
              nombre, email o teléfono
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Ingresa el monto
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Confirma con tu PIN
            </li>
          </ol>
        ),
        cta: { label: 'Transferir ahora', path: '/transfer' },
      },
      {
        id: 'wallet-receipt',
        tourId: 'receipt',
        question: '¿Cómo descargo un comprobante?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Wallet → Movimientos</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca el movimiento que te
              interesa
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Presiona{' '}
              <strong>Descargar PDF</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> El comprobante se guarda en
              tu dispositivo
            </li>
          </ol>
        ),
        cta: { label: 'Ver movimientos', path: '/wallet' },
      },
      {
        id: 'wallet-contact',
        tourId: 'contacts',
        question: '¿Cómo agrego un contacto?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Contactos</strong> en el menú
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca el botón{' '}
              <strong>+ Nuevo</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Ingresa el email o teléfono
              — si está en Tankef aparece su perfil automáticamente
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Si no está en Tankef puedes
              invitarlo o agregar su CLABE
            </li>
          </ol>
        ),
        cta: { label: 'Mis contactos', path: '/contacts' },
      },
      {
        id: 'wallet-withdraw',
        tourId: 'transfers',
        question: '¿Cómo retiro dinero a mi banco?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Wallet</strong> y toca <strong>Retirar</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Elige el monto y la cuenta
              destino
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Confirma con tu PIN o
              huella digital
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> El dinero llega en 1–2 días
              hábiles vía SPEI
            </li>
          </ol>
        ),
        cta: { label: 'Ir a mi Wallet', path: '/wallet' },
      },
    ],
  },
  {
    id: 'investments',
    icon: <TrendingUp className={ic} />,
    title: 'Inversiones',
    items: [
      {
        id: 'inv-how',
        tourId: 'investments',
        question: '¿Cómo empiezo a invertir?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Inversiones</strong> en el menú lateral
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Explora los proyectos
              disponibles — cada uno muestra tasa, plazo y nivel de riesgo
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Toca{' '}
              <strong>Solicitar</strong> en el proyecto de tu interés
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Elige el monto a invertir y
              confirma con tu PIN
            </li>
          </ol>
        ),
        cta: { label: 'Ver proyectos', path: '/investments' },
      },
      {
        id: 'inv-returns',
        tourId: 'inv-table',
        question: '¿Cómo veo mi tabla de rendimientos?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Inversiones</strong> y selecciona una inversión activa
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Baja hasta la sección{' '}
              <strong>Tabla de amortización</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Cada fila muestra: fecha,
              capital, intereses, ISR retenido y saldo restante
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Los pagos en verde ya se
              realizaron
            </li>
          </ol>
        ),
        cta: { label: 'Mis inversiones', path: '/investments' },
      },
      {
        id: 'inv-isr',
        question: '¿Qué es la retención ISR?',
        answer: (
          <p className="mt-1 text-left leading-relaxed">
            Es la retención del <strong>Impuesto Sobre la Renta</strong> (Art. 21 LIF) que Tankef
            aplica automáticamente sobre los intereses que ganas. Se descuenta directo de cada pago
            periódico. Puedes verlo desglosado en cada fila de tu tabla de amortización.
          </p>
        ),
      },
      {
        id: 'inv-statement',
        tourId: 'inv-table',
        question: '¿Cómo descargo mi estado de cuenta?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Entra a la inversión que te
              interesa
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca el botón{' '}
              <strong>Estado de cuenta</strong> en la parte superior
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Descarga el PDF con el
              historial completo de pagos
            </li>
          </ol>
        ),
        cta: { label: 'Ver inversiones', path: '/investments' },
      },
    ],
  },
  {
    id: 'savings',
    icon: <PiggyBank className={ic} />,
    title: 'Cajas de Ahorro',
    items: [
      {
        id: 'sav-open',
        tourId: 'savings-box',
        question: '¿Cómo abro una caja de ahorro?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Cajas de Ahorro</strong> en el menú
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca{' '}
              <strong>Abrir nueva caja</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Elige el producto de ahorro
              (plazo y tasa)
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Define monto y frecuencia
              de aportación
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">5.</span> Firma el contrato con tu
              NIP
            </li>
          </ol>
        ),
        cta: { label: 'Abrir caja de ahorro', path: '/savings-box/apply' },
      },
      {
        id: 'sav-contribute',
        tourId: 'sav-detail',
        question: '¿Cómo hago un abono extra?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Entra a tu caja de ahorro
              activa
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca el botón{' '}
              <strong>+ Abonar</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Ingresa el monto adicional
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Confirma con tu PIN
            </li>
          </ol>
        ),
        cta: { label: 'Mis cajas de ahorro', path: '/savings-box' },
      },
      {
        id: 'sav-returns',
        tourId: 'sav-detail',
        question: '¿Cómo veo mis rendimientos?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Entra a tu caja de ahorro
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca la pestaña{' '}
              <strong>Rendimientos</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> La tabla muestra intereses
              acumulados periodo a periodo
            </li>
          </ol>
        ),
        cta: { label: 'Ver mis cajas', path: '/savings-box' },
      },
      {
        id: 'sav-statement',
        tourId: 'sav-detail',
        question: '¿Cómo descargo mi estado de cuenta?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Entra a tu caja de ahorro
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca el botón{' '}
              <strong>Estado de cuenta</strong> (ícono de descarga)
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Se descarga un PDF con el
              historial completo
            </li>
          </ol>
        ),
        cta: { label: 'Mis cajas de ahorro', path: '/savings-box' },
      },
      {
        id: 'sav-close',
        tourId: 'sav-detail',
        question: '¿Puedo cerrar mi caja antes de tiempo?',
        answer: (
          <p className="mt-1 text-left leading-relaxed">
            Sí. Dentro de tu caja toca <strong>Cerrar caja</strong>. Se calcularán los intereses
            ganados hasta ese momento y el saldo disponible se transferirá a tu Wallet. Puede
            aplicar penalización según el producto.
          </p>
        ),
        cta: { label: 'Ver mis cajas', path: '/savings-box' },
      },
    ],
  },
  {
    id: 'tandas',
    icon: <RefreshCw className={ic} />,
    title: 'Tandas',
    items: [
      {
        id: 'tanda-how',
        question: '¿Cómo funciona una tanda?',
        answer: (
          <p className="mt-1 text-left leading-relaxed">
            Un grupo de participantes aporta un monto fijo cada ronda. En cada turno, un miembro
            recibe el pozo completo. <strong>Tankef garantiza los pagos</strong> aunque alguien no
            aporte — tú siempre recibes lo acordado.
          </p>
        ),
        cta: { label: 'Ver tandas', path: '/tandas' },
      },
      {
        id: 'tanda-join',
        tourId: 'tandas',
        question: '¿Cómo me uno a una tanda?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Tandas</strong> en el menú
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Explora tandas disponibles
              y selecciona una
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Elige tu número de turno
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Realiza el primer aporte
              para confirmar tu lugar
            </li>
          </ol>
        ),
        cta: { label: 'Ver tandas disponibles', path: '/tandas' },
      },
      {
        id: 'tanda-turn',
        tourId: 'tanda-turn',
        question: '¿Cuándo me toca cobrar?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Tandas</strong> y abre tu tanda activa
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> En el panel principal verás
              tu número de turno y la <strong>fecha estimada de cobro</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Recibirás una notificación
              cuando sea tu turno
            </li>
          </ol>
        ),
        cta: { label: 'Mis tandas', path: '/tandas' },
      },
    ],
  },
  {
    id: 'security',
    icon: <Shield className={ic} />,
    title: 'Seguridad',
    items: [
      {
        id: 'sec-pin',
        question: '¿Cómo cambio mi contraseña o PIN?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Configuración</strong> (ícono de engranaje)
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca la pestaña{' '}
              <strong>Seguridad</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Selecciona{' '}
              <strong>Cambiar contraseña</strong> o <strong>Cambiar PIN</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Confirma con tu contraseña
              actual
            </li>
          </ol>
        ),
        cta: { label: 'Ir a Seguridad', path: '/settings/security' },
      },
      {
        id: 'sec-2fa',
        question: '¿Cómo activo la verificación en dos pasos?',
        answer: (
          <ol className="mt-1 space-y-2 text-left">
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">1.</span> Ve a{' '}
              <strong>Configuración → Seguridad</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">2.</span> Toca{' '}
              <strong>Verificación en dos pasos</strong>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">3.</span> Descarga una app de
              autenticación (Google Authenticator, Authy)
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary-600">4.</span> Escanea el código QR y
              confirma con el código generado
            </li>
          </ol>
        ),
        cta: { label: 'Activar 2FA', path: '/settings/security' },
      },
      {
        id: 'sec-limits',
        question: '¿Cuáles son mis límites de operación?',
        answer: (
          <p className="mt-1 text-left leading-relaxed">
            Tus límites dependen de tu nivel KYC. Con verificación básica puedes operar hasta $5,000
            MXN diarios. Con verificación completa (INE + foto) suben a $50,000 MXN. Ve a{' '}
            <strong>Configuración → Seguridad → Límites</strong> para ver los tuyos.
          </p>
        ),
        cta: { label: 'Ver mis límites', path: '/settings/security' },
      },
    ],
  },
];

// ─── Route → section mapping ──────────────────────────────────────────────────

/**
 * Returns the section ID and greeting that best match the current pathname.
 * Import this in the layout to pass contextual data to HelpChat.
 *
 * ```ts
 * const { sectionId, greeting } = getHelpContext(location.pathname);
 * ```
 */
export function getHelpContext(pathname: string): {
  sectionId?: string;
  greeting: string;
} {
  if (
    pathname.startsWith('/wallet') ||
    pathname.startsWith('/transfer') ||
    pathname.startsWith('/payments') ||
    pathname.startsWith('/contacts')
  ) {
    return {
      sectionId: 'wallet',
      greeting:
        '¡Hola! Veo que estás en tu Wallet. ¿Cómo puedo ayudarte con depósitos, retiros o transferencias?',
    };
  }
  if (pathname.startsWith('/investments')) {
    return {
      sectionId: 'investments',
      greeting:
        '¡Hola! Veo que estás en Inversiones. Te explico cómo ver tus rendimientos, tabla de amortización y más.',
    };
  }
  if (pathname.startsWith('/savings-box') || pathname.startsWith('/rotating-savings')) {
    return {
      sectionId: 'savings',
      greeting:
        '¡Hola! Veo que estás en Cajas de Ahorro. Te guío para abrir una caja, hacer abonos y descargar tu estado de cuenta.',
    };
  }
  if (pathname.startsWith('/tandas')) {
    return {
      sectionId: 'tandas',
      greeting:
        '¡Hola! Veo que estás en Tandas. Te explico cómo funcionan, cómo unirte y cuándo es tu turno de cobrar.',
    };
  }
  if (pathname.startsWith('/settings')) {
    return {
      sectionId: 'security',
      greeting:
        '¡Hola! Estás en Configuración. Te ayudo con seguridad, límites y verificación en dos pasos.',
    };
  }
  return {
    sectionId: undefined,
    greeting: '¡Hola! Soy Cash, tu asistente de Tankef. ¿En qué te puedo ayudar hoy?',
  };
}

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

function FaqItem({
  item,
  onNavigate,
  onStartTour,
}: {
  item: HelpFaqItem;
  onNavigate?: (path: string) => void;
  onStartTour?: (tourId: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-tkf-line last:border-0">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm font-medium text-tkf-fg hover:text-primary-600 focus-visible:outline-none"
      >
        <span>{item.question}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-tkf-fg-subtle transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="pb-4">
          <div className="text-sm leading-relaxed text-tkf-fg-muted">{item.answer}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {item.cta && onNavigate && (
              <button
                type="button"
                onClick={() => onNavigate(item.cta!.path)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100 focus-visible:outline-none"
              >
                {item.cta.label}
                <ChevronRight className="h-3 w-3" />
              </button>
            )}
            {item.tourId && onStartTour && (
              <button
                type="button"
                onClick={() => onStartTour(item.tourId!)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-tkf-surface-muted px-3 py-1.5 text-xs font-semibold text-tkf-fg-muted transition-colors hover:bg-tkf-surface hover:text-tkf-fg focus-visible:outline-none"
              >
                <MessageCircleQuestion className="h-3 w-3" />
                Ver tutorial
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section accordion ────────────────────────────────────────────────────────

function HelpSectionAccordion({
  section,
  initiallyOpen = false,
  onNavigate,
  onStartTour,
}: {
  section: HelpSection;
  initiallyOpen?: boolean;
  onNavigate?: (path: string) => void;
  onStartTour?: (tourId: string) => void;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    const opening = !open;
    setOpen(opening);
    if (opening) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 150);
    }
  };

  return (
    <div
      ref={containerRef}
      className="overflow-hidden rounded-xl border border-tkf-line bg-tkf-surface"
    >
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-tkf-surface-subtle focus-visible:outline-none"
      >
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            section.iconVariant === 'primary' ? 'bg-primary-500 text-white' : 'text-tkf-fg'
          )}
        >
          {section.icon}
        </span>
        <span className="flex-1 text-sm font-semibold text-tkf-fg">{section.title}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-tkf-fg-subtle transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="border-t border-tkf-line px-4">
          {section.items.map(item => (
            <FaqItem key={item.id} item={item} onNavigate={onNavigate} onStartTour={onStartTour} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── HelpChat panel ───────────────────────────────────────────────────────────

export function HelpChat({
  isOpen,
  onClose,
  onNavigate,
  onStartTour,
  sections = DEFAULT_HELP_SECTIONS,
  initialSectionId,
  greeting = '¡Hola! Soy Cash, tu asistente de Tankef. ¿En qué te puedo ayudar?',
  className,
}: HelpChatProps) {
  const handleNavigate = (path: string) => {
    onNavigate?.(path);
    onClose();
  };

  const handleStartTour = (tourId: string) => {
    onClose();
    onStartTour?.(tourId);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      size="xl"
      closeOnBackdropClick
      closeOnEscape
      className={className}
    >
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-tkf-line px-4 py-3">
        <div className="flex items-center gap-3">
          <Mascot emotion="callcenter" size="sm" animated={false} />
          <div>
            <p className="text-sm font-bold text-tkf-fg">Cash</p>
            <p className="text-xs text-success-600">● En línea</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar ayuda"
          className="rounded-full p-1.5 text-tkf-fg-subtle transition-colors hover:bg-tkf-surface-muted hover:text-tkf-fg focus-visible:outline-none"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Greeting bubble */}
      <div className="shrink-0 bg-tkf-surface-subtle px-4 py-4">
        <div className="flex items-start gap-3">
          <Mascot emotion="callcenter" size="xs" animated={false} className="mt-0.5 shrink-0" />
          <div className="rounded-2xl rounded-tl-sm border border-tkf-line bg-tkf-surface px-4 py-3 text-sm leading-relaxed text-tkf-fg shadow-sm">
            {greeting}
          </div>
        </div>
      </div>

      {/* FAQ sections */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-tkf-fg-subtle">
          Temas de ayuda
        </p>
        <div className="space-y-2">
          {sections.map(section => (
            <HelpSectionAccordion
              key={section.id}
              section={section}
              initiallyOpen={section.id === initialSectionId}
              onNavigate={handleNavigate}
              onStartTour={onStartTour ? handleStartTour : undefined}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-6 rounded-xl border border-tkf-line-light bg-tkf-surface-subtle px-4 py-4 text-center">
          <Mascot emotion="hint" size="sm" animated className="mx-auto mb-2" />
          <p className="text-xs text-tkf-fg-muted">
            ¿No encontraste lo que buscabas?{' '}
            <a
              href="mailto:hola@tankef.com"
              className="font-medium text-primary-600 hover:underline"
            >
              Escríbenos
            </a>
          </p>
        </div>
      </div>
    </Drawer>
  );
}

// ─── HelpChatTrigger ──────────────────────────────────────────────────────────

export interface HelpChatTriggerProps {
  onClick: () => void;
  beacon?: boolean;
  tooltip?: string;
  compact?: boolean;
  className?: string;
}

export function HelpChatTrigger({
  onClick,
  beacon = false,
  tooltip = 'Centro de ayuda',
  compact = false,
  className,
}: HelpChatTriggerProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={tooltip}
      title={tooltip}
      className={cn(
        'relative inline-flex items-center gap-2 rounded-full transition-colors',
        'text-tkf-fg-muted hover:text-primary-600 focus-visible:outline-none',
        compact
          ? 'p-1.5 hover:bg-tkf-surface-muted'
          : 'border border-tkf-line bg-tkf-surface px-4 py-2 text-sm font-medium shadow-sm hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700',
        className
      )}
    >
      <MessageCircleQuestion className={cn('shrink-0', compact ? 'h-5 w-5' : 'h-4 w-4')} />
      {!compact && <span>Ayuda</span>}
      {beacon && (
        <span aria-hidden className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary-500" />
        </span>
      )}
    </button>
  );
}

// ─── FloatingHelpButton ───────────────────────────────────────────────────────

export interface FloatingHelpButtonProps {
  onClick: () => void;
  hidden?: boolean;
  /** 'icon' = clean FAB (default) | 'mascot' = Cash circle */
  variant?: 'icon' | 'mascot';
}

const helpRingKeyframes = `
@keyframes help-ring {
  0%, 100% { box-shadow: 0 0 0 0 rgba(41,214,178,0); }
  8%        { box-shadow: 0 0 0 8px rgba(41,214,178,0.28); }
  20%       { box-shadow: 0 0 0 14px rgba(41,214,178,0); }
}
`;

export function FloatingHelpButton({
  onClick,
  hidden = false,
  variant = 'icon',
}: FloatingHelpButtonProps) {
  if (hidden) return null;

  if (variant === 'mascot') {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label="Abrir centro de ayuda"
        className={cn(
          'fixed bottom-6 right-6 z-chat flex h-14 w-14 items-center justify-center',
          'rounded-full bg-tkf-surface shadow-lg ring-1 ring-tkf-line',
          'transition-transform duration-150 hover:scale-110 active:scale-95',
          'focus-visible:outline-none'
        )}
      >
        <Mascot emotion="callcenter" size="sm" animated={false} alt="Abrir ayuda" />
      </button>
    );
  }

  return (
    <>
      <style>{helpRingKeyframes}</style>
      <button
        type="button"
        onClick={onClick}
        aria-label="Abrir centro de ayuda"
        style={{ animation: 'help-ring 5s ease-out infinite' }}
        data-tour="help-btn"
        className={cn(
          'fixed bottom-6 right-6 z-chat',
          'flex h-12 w-12 items-center justify-center rounded-full',
          'bg-primary-500 text-tkf-fg shadow-md',
          'transition-transform duration-150 hover:scale-110 active:scale-95',
          'focus-visible:outline-none'
        )}
      >
        <MessageCircleQuestion className="h-5 w-5" />
      </button>
    </>
  );
}
